import { useState, useEffect, useRef } from 'react';

/**
 * Hook to determine maximum number of tabs to show based on container width
 * Returns 5, 4, 3, or 2 tabs depending on available space
 */
const useResponsiveTabCount = () => {
  const [maxTabs, setMaxTabs] = useState(5);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Debounce timer
    let timeoutId;

    const updateTabCount = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.offsetWidth;
      
      // Calculate max tabs based on width
      // >= 360px: 5 tabs
      // >= 290px: 4 tabs
      // >= 220px: 3 tabs
      // < 220px: 2 tabs
      if (width >= 360) {
        setMaxTabs(5);
      } else if (width >= 290) {
        setMaxTabs(4);
      } else if (width >= 220) {
        setMaxTabs(3);
      } else {
        setMaxTabs(2);
      }
    };

    // Initial calculation
    updateTabCount();

    // Debounced resize handler
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateTabCount, 300);
    };

    // Use ResizeObserver for better performance if available
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(containerRef.current);
      
      return () => {
        resizeObserver.disconnect();
        clearTimeout(timeoutId);
      };
    } else {
      // Fallback to window resize event
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  return { maxTabs, containerRef };
};

// Named export for tests / callsites that import { useResponsiveTabCount }.
export { useResponsiveTabCount };

export default useResponsiveTabCount;

